// router.ts — minimal hash-based view switching (no router library, per spec).
import { useEffect, useState } from 'preact/hooks';

export type View = 'cockpit' | 'vergleich' | 'szenarien' | 'historie' | 'einstellungen' | 'info' | 'daten';

/** Every top-level route in one place — the drawer nav renders straight off this array,
 * so adding a future route is a one-line addition here (plus the icon in icons.tsx and
 * the view branch in app.tsx). `icon` is a name looked up by <NavIcon> (components/icons.tsx),
 * not JSX, so this array stays plain data. */
export interface NavItem {
  hash: View;
  label: string;
  icon: View;
}

export const NAV_ITEMS: NavItem[] = [
  { hash: 'cockpit', label: 'Cockpit', icon: 'cockpit' },
  { hash: 'vergleich', label: 'Vergleich', icon: 'vergleich' },
  { hash: 'szenarien', label: 'Szenarien', icon: 'szenarien' },
  { hash: 'historie', label: 'Historie', icon: 'historie' },
  { hash: 'einstellungen', label: 'Einstellungen', icon: 'einstellungen' },
  { hash: 'info', label: 'Info & Steuer', icon: 'info' },
  { hash: 'daten', label: 'Daten', icon: 'daten' },
];

const KNOWN_VIEWS: readonly string[] = NAV_ITEMS.map((item) => item.hash);

function parseHash(): View {
  const hash = typeof location !== 'undefined' ? location.hash.replace(/^#\/?/, '') : '';
  // Unknown/empty hash -> #cockpit. This also covers in-page anchors an unrelated
  // feature might one day push onto location.hash — anything not in NAV_ITEMS falls
  // back to the default route rather than 404-ing the whole shell.
  return (KNOWN_VIEWS.includes(hash) ? hash : 'cockpit') as View;
}

export function useHashRoute(): [View, (view: View) => void] {
  const [view, setView] = useState<View>(() => parseHash());

  useEffect(() => {
    function onHashChange() {
      setView(parseHash());
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  function navigate(next: View) {
    location.hash = `/${next}`;
    setView(next);
  }

  return [view, navigate];
}
